import React, { useCallback, useState } from 'react';

import { Box, Flex, useTheme } from '@chakra-ui/react';
import { InputMaterialUITextArea } from 'components/RabbitOKRSidePanel/Components/TextAreaMaterialUI';

import { Description } from 'assets/assetsV1/iconComponents';

import { MAX_OKR_DESCRIPTION_LENGTH } from 'constants/ProjectConstants';

import InputLabel from 'uiToolkitV1/InputLabel';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { useLocalState } from './useLocalState';

const CharacterLimitErrorUi = ({ characterCount, maxCharacterLimit = 1024 }) => (
  <Flex className="character-count" mt="4px" justifyContent="space-between">
    {characterCount > maxCharacterLimit ? (
      <TextBodyMedium color="functional.C62C02" align="left">
        Character limit exceeded.
      </TextBodyMedium>
    ) : null}

    <TextBodyMedium ml="auto" color={characterCount > maxCharacterLimit ? 'functional.C62C02' : 'text.g3'}>
      {characterCount}/{maxCharacterLimit}
    </TextBodyMedium>
  </Flex>
);

export const OKRDescriptionComponent = ({
  description,
  onChange,
  onChangeWithoutBlur = () => {},
  isReadOnly = true,
  error = '',
  setError = () => {},
  isLeftIcon = false,
}) => {
  const theme = useTheme();
  const [localDescription, setLocalDescription] = useLocalState(description ?? '');

  const hasError = error.length > 0;

  const safeOnChange = () => {
    if (hasError) return;
    if (description === localDescription) return;
    onChange(localDescription);
  };

  const handleChange = (e) => {
    const descriptionText = e?.target?.value;
    setLocalDescription(descriptionText);
    validateValue(descriptionText);
    onChangeWithoutBlur(descriptionText);
  };

  const validateValue = useCallback(
    (description) => {
      /* check if some error is present. */

      if (description?.length > MAX_OKR_DESCRIPTION_LENGTH) {
        setError(`Max ${MAX_OKR_DESCRIPTION_LENGTH} characters`);
        return;
      }

      setError('');
    },
    [setError]
  );

  const isDescriptionEmpty = localDescription?.length === 0;

  return (
    <Box>
      {isReadOnly ? (
        <TextBodyMedium color={isDescriptionEmpty ? theme.colors.text.g2 : theme.colors.text.g1}>
          {isDescriptionEmpty ? 'None' : localDescription}
        </TextBodyMedium>
      ) : (
        <Box display="flex" flexDirection="column">
          <Flex position="relative" zIndex={0}>
            {isLeftIcon && <Description color="text.g2" fill="none" boxSize="18px" marginRight="-18px" zIndex="1" />}
            <InputMaterialUITextArea
              value={localDescription}
              onChange={handleChange}
              placeholder="Description"
              onBlur={safeOnChange}
              width="100%"
              minRows={1}
              maxRows={5}
              className="okr-description"
              paddingLeft={isLeftIcon ? '30px' : '0px'}
            />
          </Flex>
          <CharacterLimitErrorUi
            characterCount={localDescription?.length}
            maxCharacterLimit={MAX_OKR_DESCRIPTION_LENGTH}
          />
        </Box>
      )}
    </Box>
  );
};

export const OKRDescription = ({ description, onChange, isReadOnly = true }) => {
  const [error, setError] = useState('');

  return (
    <InputLabel labelText="Description" captionText={error} captionVariant="error">
      <OKRDescriptionComponent
        description={description}
        onChange={onChange}
        isReadOnly={isReadOnly}
        error={error}
        setError={setError}
      />
    </InputLabel>
  );
};
